import { DataSourceRequestState, FilterDescriptor, State as GridState, toDataSourceRequestString } from '@progress/kendo-data-query';
import { Button } from '@progress/kendo-react-buttons';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { GridColumn as Column, Grid, GridCustomCellProps, GridCustomRowProps, GridDataStateChangeEvent, GridToolbar } from '@progress/kendo-react-grid';
import { TextArea } from '@progress/kendo-react-inputs';
import { arrowRotateCwIcon, filterClearIcon } from '@progress/kendo-svg-icons';
import { ReactElement, useCallback, useEffect, useState } from 'react';
import { CashAdvanceRequestStatus } from '../../TypeGen/Assets/cash-advance-request-status';
import { CashAdvanceRequestViewModel } from '../../TypeGen/Assets/cash-advance-request-view-model';
import { JsonResponse } from '../../TypeGen/json-response';
import { GridLoadingPanel } from '../../components/GridLoadingPanel';
import DateCell from '../../components/cells/DateCell';
import { fetchApi } from '../../services/api';
import { openWindowPerc } from '../../services/openWindow';
import { Title } from '../../utils/title';
import { dateFormatter } from '../../utils/utils';
import DriverAutoComplete from '../AssetDrivers/DriverAutoComplete';
import OwnerAutoComplete from '../AssetOwners/OwnerAutoComplete';
import VehicleAutoComplete from '../AssetVehicles/VehicleAutoComplete';
import AdvanceCashDialog from './Driver/AdvanceCashDialog';
import CardHistoryDialog from './Driver/CardHistoryDialog';

const DriverNumberCell = (props: GridCustomCellProps) => {
  if (!props.field || props.rowType !== 'data') return null;
  const dataItem = props.dataItem as CashAdvanceRequestViewModel;
  return (
    <td {...props.tdProps}>
      <a
        href="#"
        style={{ color: '#007bff' }}
        onClick={(e) => {
          e.preventDefault();
          openWindowPerc(`/Assets/Driver/${dataItem.DriverId}`);
        }}
      >
        {dataItem.DriverNumber}
      </a>
    </td>
  );
};

const OwnerNumberCell = (props: GridCustomCellProps) => {
  if (!props.field || props.rowType !== 'data') return null;
  const dataItem = props.dataItem as CashAdvanceRequestViewModel;
  return (
    <td {...props.tdProps}>
      <a
        href="#"
        style={{ color: '#007bff' }}
        onClick={(e) => {
          e.preventDefault();
          openWindowPerc(`/Assets/Owner/${dataItem.OwnerId}`);
        }}
      >
        {dataItem.OwnerNumber}
      </a>
    </td>
  );
};

const VehicleNumberCell = (props: GridCustomCellProps) => {
  if (!props.field || props.rowType !== 'data') return null;
  const dataItem = props.dataItem as CashAdvanceRequestViewModel;
  if (!dataItem.VehicleId) return <td {...props.tdProps}>{dataItem.VehicleNumber}</td>;
  return (
    <td {...props.tdProps}>
      <a
        href="#"
        style={{ color: '#007bff' }}
        onClick={(e) => {
          e.preventDefault();
          openWindowPerc(`/Assets/Vehicle/${dataItem.VehicleId}`);
        }}
      >
        {dataItem.VehicleNumber}
      </a>
    </td>
  );
};

const CashAdvanceRequests = () => {
  const initialState: GridState = {
    skip: 0,
    take: 50,
    sort: [{ field: 'RequestedDateTime', dir: 'desc' }],
    filter: { logic: 'and', filters: [] }
  };

  const [dataState, setDataState] = useState<DataSourceRequestState>(initialState);
  const [requests, setRequests] = useState<CashAdvanceRequestViewModel[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rejectRequest, setRejectRequest] = useState<CashAdvanceRequestViewModel | null>(null);
  const [takeControlRequest, setTakeControlRequest] = useState<CashAdvanceRequestViewModel | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [filterDriverId, setFilterDriverId] = useState<number | null>(null);
  const [filterOwnerId, setFilterOwnerId] = useState<number | null>(null);
  const [filterVehicleId, setFilterVehicleId] = useState<number | null>(null);
  const [filterStatus, setFilterStatus] = useState<number | null>(null);
  const [advanceRequest, setAdvanceRequest] = useState<CashAdvanceRequestViewModel | null>(null);
  const [cardHistoryRequest, setCardHistoryRequest] = useState<CashAdvanceRequestViewModel | null>(null);

  const statusOptions = Object.keys(CashAdvanceRequestStatus)
    .filter(k => isNaN(Number(k)))
    .map(k => ({ text: k.replace(/([A-Z])/g, ' $1').trim(), value: (CashAdvanceRequestStatus as any)[k] }));

  const updateOrAddFilter = (filterDescriptor: FilterDescriptor) => {
    const filters = dataState.filter?.filters as FilterDescriptor[];
    const hasFilter = filters.some(f => f.field === filterDescriptor.field);
    if (hasFilter) {
      const updated = filters.map(f => f.field === filterDescriptor.field ? filterDescriptor : f);
      setDataState({ ...dataState, filter: { ...dataState.filter, filters: updated } });
    } else {
      filters.push(filterDescriptor);
      setDataState({ ...dataState });
    }
  };

  const removeFilter = (field: string) => {
    const filters = dataState.filter?.filters as FilterDescriptor[];
    if (filters.some(f => f.field === field)) {
      dataState.filter.filters = filters.filter(f => f.field !== field);
      setDataState({ ...dataState });
    }
  };

  const fetchData = useCallback(() => {
    setLoading(true);
    const queryStr = toDataSourceRequestString(dataState);
    fetchApi(`/api/Asset/CashAdvanceRequests?${queryStr}`, {}, 'POST')
      .then(({ Data, Total }) => {
        setRequests(Data);
        setTotal(Total);
        setLoading(false);
      });
  }, [dataState]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onDataStateChange = (e: GridDataStateChangeEvent) => {
    setDataState(e.dataState);
  };

  const clearState = () => {
    setFilterDriverId(null);
    setFilterOwnerId(null);
    setFilterVehicleId(null);
    setFilterStatus(null);
    setDataState(initialState);
  };

  const acceptRequest = (request: CashAdvanceRequestViewModel) => {
    setAdvanceRequest(request);
  };

  const openRejectDialog = (request: CashAdvanceRequestViewModel) => {
    setRejectRequest(request);
    setRejectReason('');
  };

  const closeRejectDialog = () => {
    setRejectRequest(null);
    setRejectReason('');
  };

  const openTakeControlDialog = (request: CashAdvanceRequestViewModel) => {
    setTakeControlRequest(request);
  };

  const closeTakeControlDialog = () => {
    setTakeControlRequest(null);
  };

  const CardHistoryCell = (props: GridCustomCellProps) => {
    if (!props.field || props.rowType !== 'data') return null;
    const dataItem = props.dataItem as CashAdvanceRequestViewModel;
    if (!dataItem.CardNumber) return <td {...props.tdProps} />;
    return (
      <td {...props.tdProps}>
        <a
          href="#"
          style={{ color: '#007bff' }}
          onClick={(e) => {
            e.preventDefault();
            setCardHistoryRequest(dataItem);
          }}
        >
          {dataItem.CardNumber}
        </a>
      </td>
    );
  };

  const confirmReject = () => {
    if (!rejectRequest) return;
    setSaving(true);
    const payload = {
      CashAdvanceRequestID: rejectRequest.CashAdvanceRequestID,
      Reason: rejectReason
    };
    fetchApi('/api/Asset/RejectCashAdvanceRequest', payload, 'POST')
      .then((response: JsonResponse) => {
        if (response.Success) {
          fetchData();
          closeRejectDialog();
        } else {
          alert(response.ErrorMessage);
        }
      })
      .finally(() => setSaving(false));
  };

  const approveForOwner = () => {
    if (!takeControlRequest) return;
    setSaving(true);
    const payload = {
      OwnerId: takeControlRequest.OwnerId,
      CashAdvanceRequestID: takeControlRequest.CashAdvanceRequestID,
      TakeControlFromOwner: true
    };
    fetchApi('/api/Asset/ApproveCashAdvanceRequestOwner', payload, 'POST')
      .then((response: JsonResponse) => {
        if (response.Success) {
          fetchData();
          closeTakeControlDialog();
        } else {
          alert(response.ErrorMessage);
        }
      })
      .finally(() => setSaving(false));
  };

  const ProcessedCell = (props: GridCustomCellProps) => {
    const data = props.dataItem as CashAdvanceRequestViewModel;

    if (data.Status === CashAdvanceRequestStatus.PendingOwner) {
      return <td {...props.tdProps}>
        <div><Button disabled={saving} onClick={() => openTakeControlDialog(data)}>Assume Control/Proceed as Approver</Button></div>
        <div><strong>Status:</strong> {data.StatusName} on {dateFormatter(data.LastUpdatedDateTime)}</div>
      </td>;
    }

    if (data.Status === CashAdvanceRequestStatus.PendingUser) {
      return <td {...props.tdProps}>
        <Button themeColor="success" disabled={saving} onClick={() => acceptRequest(data)}>ACCEPT</Button>
        <Button themeColor="error" disabled={saving} onClick={() => openRejectDialog(data)} className="ml-2">REJECT</Button>
      </td>;
    }

    if (data.Status === CashAdvanceRequestStatus.RejectedUser || data.Status === CashAdvanceRequestStatus.Processed) {
      if (data.Status === CashAdvanceRequestStatus.RejectedUser) {
        return <td {...props.tdProps}>
          <strong>Status:</strong> {data.StatusName} on {dateFormatter(data.UserApprovedDateTime)} - {data.UserName}
        </td>;
      } else {
        return <td {...props.tdProps}>
          <strong>Status:</strong> {data.StatusName} on {dateFormatter(data.ProcessedDateTime)} - {data.UserName}
        </td>;
      }
    }

    return <td {...props.tdProps}>
      <strong>Status:</strong> {data.StatusName} on {dateFormatter(data.LastUpdatedDateTime)}
    </td>;
  };

  const rowRender = (rowProps: GridCustomRowProps) => {
    const data = rowProps.dataItem as CashAdvanceRequestViewModel;
    const notes = [] as ReactElement[];
    if (data.DriverNote?.trim()) {
      notes.push(<div key="driver"><strong>Driver Note:</strong> {data.DriverNote}</div>);
    }
    if (data.OwnerNote?.trim()) {
      notes.push(<div key="owner"><strong>Owner Note:</strong> {data.OwnerNote}</div>);
    }
    if (data.UserNote?.trim()) {
      notes.push(<div key="user"><strong>User Note:</strong> {data.UserNote}</div>);
    }
    if (notes.length === 0) {
      return <tr {...rowProps.trProps}>{rowProps.children}</tr>;
    }
    // const colSpan = row.props.children ? row.props.children.length : 1;
    // className="text-right"
    // <td colSpan={colSpan - 1}></td>
    //<td>{notes}</td>
    return (
      <>
        <tr {...rowProps.trProps}>
          {rowProps.children}
        </tr>
        <tr key={data.CashAdvanceRequestID + '-notes'} className="k-detail-row">
          <td colSpan={8}>{notes}</td>
        </tr>
      </>
    );
  };

  return (
    <>
      {loading && <GridLoadingPanel />}
      <Title string="Cash Advance Requests" />
      <Grid
        data={requests}
        {...dataState}
        total={total}
        pageable
        sortable
        //filterable
        dataItemKey="CashAdvanceRequestID"
        onDataStateChange={onDataStateChange}
        rows={{ data: rowRender }}
      //style={{ height: '600px' }}
      >
        <GridToolbar>
          <DriverAutoComplete
            style={{ width: '200px' }}
            selectedDriverID={filterDriverId ?? undefined}
            onSelectedDriver={(e) => {
              if (e?.DriverID > 0) {
                updateOrAddFilter({ field: 'DriverId', operator: 'eq', value: e.DriverID });
              } else {
                removeFilter('DriverId');
              }
              setFilterDriverId(e?.DriverID ?? null);
            }}
          />
          <OwnerAutoComplete
            style={{ width: '200px' }}
            selectedOwnerID={filterOwnerId ?? undefined}
            onSelectedOwner={(e) => {
              if (e?.OwnerID > 0) {
                updateOrAddFilter({ field: 'OwnerId', operator: 'eq', value: e.OwnerID });
              } else {
                removeFilter('OwnerId');
              }
              setFilterOwnerId(e?.OwnerID ?? null);
            }}
          />
          <VehicleAutoComplete
            style={{ width: '200px' }}
            selectedVehicleID={filterVehicleId ?? undefined}
            onSelectedVehicle={(e) => {
              if (e?.VehicleID > 0) {
                updateOrAddFilter({ field: 'VehicleId', operator: 'eq', value: e.VehicleID });
              } else {
                removeFilter('VehicleId');
              }
              setFilterVehicleId(e?.VehicleID ?? null);
            }}
          />
          <DropDownList
            style={{ width: '200px' }}
            data={statusOptions}
            textField="text"
            dataItemKey="value"
            value={statusOptions.find(x => x.value === filterStatus) ?? null}
            defaultItem={{ text: 'All Statuses', value: null }}
            onChange={(e) => {
              const val = e.target.value?.value ?? null;
              setFilterStatus(val);
              if (val != null) {
                updateOrAddFilter({ field: 'Status', operator: 'eq', value: val });
              } else {
                removeFilter('Status');
              }
            }}
          />
          <Button svgIcon={arrowRotateCwIcon} onClick={fetchData} />
          <Button svgIcon={filterClearIcon} onClick={clearState} />
        </GridToolbar>
        <Column field="DriverNumber" title="Driver" width={120} cells={{ data: DriverNumberCell }} />
        <Column field="VehicleNumber" title="Vehicle" width={120} cells={{ data: VehicleNumberCell }} />
        <Column field="OwnerNumber" title="Owner" width={120} cells={{ data: OwnerNumberCell }} />
        {/*<Column field="TripId" title="Trip" width={100} />*/}
        <Column field="Amount" title="Amount" width={120} format='{0:c2}' />
        <Column field="RequestedDateTime" title="Requested On" filter="date" cells={{ data: DateCell }} />
        {/*<Column field="OwnerApproved" title="Owner Approved" width={150} />*/}
        {/*<Column field="OwnerApprovedDateTime" title="Owner Approved Date" filter="date" cells={{ data: DateCell }} />*/}
        {/*<Column field="UserApproved" title="User Approved" width={150} />*/}
        {/*<Column field="UserApprovedDateTime" title="User Approved Date" filter="date" cells={{ data: DateCell }} />        */}
        <Column field="CardNumber" title="EFS Card History" width={160} cells={{ data: CardHistoryCell }} />
        <Column field="ProcessedConfirmationCode" title="EFS Confirmation Code" width={160} />
        <Column field="ProcessedDateTime" title="Action/Status" cells={{ data: ProcessedCell }} />
      </Grid>
      {takeControlRequest && <Dialog title="Assume Control" onClose={closeTakeControlDialog}>
        <p style={{ margin: '25px', textAlign: 'center' }}>Are you sure you want to assume control from owner?</p>
        <DialogActionsBar>
          <Button disabled={saving} onClick={closeTakeControlDialog}>Cancel</Button>
          <Button themeColor="primary" disabled={saving} onClick={approveForOwner}>Assume Control From Owner</Button>
        </DialogActionsBar>
      </Dialog>
      }
      {rejectRequest &&
        <Dialog title="Reject Request" onClose={closeRejectDialog}>
          <TextArea
            autoFocus
            value={rejectReason}
            placeholder="Reason"
            onChange={(e) => setRejectReason(e.value)}
          />
          <DialogActionsBar>
            <Button disabled={saving} onClick={closeRejectDialog}>Cancel</Button>
            <Button themeColor="error" disabled={saving} onClick={confirmReject}>Reject</Button>
          </DialogActionsBar>
        </Dialog>
      }
      {advanceRequest &&
        <AdvanceCashDialog
          DriverID={advanceRequest.DriverId}
          DriverNumber={advanceRequest.DriverNumber}
          DriverName={advanceRequest.DriverName}
          CardNumber={advanceRequest.CardNumber}
          CashAdvanceRequestID={advanceRequest.CashAdvanceRequestID}
          Amount={advanceRequest.Amount}
          CloseDialog={() => { setAdvanceRequest(null); fetchData(); }}
        />
      }
      {cardHistoryRequest && (
        <CardHistoryDialog
          DriverID={cardHistoryRequest.DriverId}
          DriverNumber={cardHistoryRequest.DriverNumber}
          DriverName={cardHistoryRequest.DriverName}
          CardNumber={cardHistoryRequest.CardNumber}
          CloseDialog={() => setCardHistoryRequest(null)}
        />
      )}
    </>
  );
};

export default CashAdvanceRequests;

